"use client";

import { toast } from "sonner";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCart, comboLookupKey } from "@/src/contexts/CartContext";
import { SafeImage } from "@/src/components/SafeImage";
import { useTenant } from "@/src/contexts/TenantContext";
import { normalizeImageUrl } from "@/src/utils/url";
import type { ComboV3, OutletDetails, Product, Tenant } from "@/src/types/tenant";
import { ProductDescription } from "@/src/app/shop/product/[id]/components/ProductDescription";
import { isProductSoldOut } from "@/src/utils/products/stock";
import { ProductHighlights, storeHighlights } from "@/src/app/shop/components/ProductHighlights";

interface ComboDetailClientProps {
  combo: ComboV3;
  outletId: string;
  prefix: string;
  tenant: Tenant;
  outlet: OutletDetails | null;
  products: Product[];
}

export default function ComboDetailClient({
  combo,
  outletId,
  prefix,
  tenant,
  outlet,
  products,
}: ComboDetailClientProps) {
  const router = useRouter();
  const { setTenant } = useTenant();
  const { addItem, addCombo, getItemQuantity, updateQuantity, removeItem } = useCart();

  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  // Thumbnail is normally the first entry of imageUrls (the CRM sets
  // `thumbnail: uploadedImageUrls[0]` on save), so prepending it would
  // duplicate the first image in the gallery. Build a deduped list keyed by
  // URL, with thumbnail first if it isn't already in the array.
  const allImages = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const candidates = [combo.thumbnail, ...(combo.imageUrls ?? [])].filter(Boolean) as string[];
    for (const url of candidates) {
      if (!seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    }
    return out;
  }, [combo.thumbnail, combo.imageUrls]);

  const mrp = combo.pricing?.mrp ?? 0;
  const sellingPrice = combo.pricing?.sellingPrice ?? 0;
  const stockQuantity = combo.inventory?.stockQuantity ?? 0;
  const savings = combo.savings ?? Math.max(0, mrp - sellingPrice);
  const discountPct = combo.discountPct ?? (mrp > sellingPrice && mrp > 0
    ? Math.round(((mrp - sellingPrice) / mrp) * 100)
    : 0);

  const comboProductId = comboLookupKey(combo._id);
  const cartQty = getItemQuantity(comboProductId);
  const inStock = stockQuantity > 0;
  const tenantId = combo.tenantId || (tenant.tenantId !== undefined ? String(tenant.tenantId) : undefined);

  useEffect(() => {
    setTenant({
      storeName: outlet?.outletName || tenant.tenantName,
      logoUrl: normalizeImageUrl(tenant.additionalInfo?.logoUrl),
      outletId,
      prefix,
      minCartValue: outlet?.minCartValue ?? null,
      city: outlet?.city ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLightboxOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  const handleAddToCart = () => {
    if (!tenantId) {
      console.warn("[ComboDetail] missing tenantId — cannot add to cart");
      return;
    }
    addCombo({
      comboId: combo._id,
      outletId,
      tenantId,
      quantity: 1,
    }).catch((err) => toast.error((err as Error)?.message || "Couldn't add to cart"));
  };

  const handleBuyNow = () => {
    handleAddToCart();
    router.push(`/shop/checkout?prefix=${prefix}`);
  };

  const handleQtyChange = (qty: number) => {
    if (qty <= 0) {
      removeItem(comboProductId);
    } else {
      updateQuantity(comboProductId, qty);
    }
  };

  const handleProductAdd = (product: Product) => {
    if (!tenantId) {
      console.warn("[ComboDetail] missing tenantId — cannot add related product");
      return;
    }
    const price = product.pricing?.offerPrice ?? product.pricing?.sellingPrice ?? 0;
    addItem({
      productId: product._id,
      outletId,
      tenantId,
      name: product.name,
      price,
      mrp: product.pricing?.mrp ?? price,
      quantity: 1,
      image: product.images?.thumbnail || undefined,
    });
  };

  const handleProductQtyChange = (productId: string, qty: number) => {
    if (qty <= 0) removeItem(productId);
    else updateQuantity(productId, qty);
  };

  return (
    <div className="min-h-screen bg-white text-[#1D1D1D]">
      {/* Breadcrumb */}
      <div className="border-b border-[#F0EDE6]">
        <div className="mx-auto flex max-w-[1280px] items-center gap-1.5 px-6 py-3 text-[12px] text-[#8A8A85]">
          <Link href={`/shop?prefix=${prefix}`} className="hover:text-[#1D1D1D] transition cursor-pointer">
            Home
          </Link>
          <span>/</span>
          <span>
            Combo
          </span>
          <span>/</span>
          <span className="text-[#1D1D1D] font-medium line-clamp-1">{combo.name}</span>
        </div>
      </div>

      {/* Main layout */}
      <div className="mx-auto max-w-[1280px] px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1fr_1fr] lg:grid-cols-[55%_45%]">

          {/* Left: image gallery */}
          <div className="md:sticky md:top-20 md:self-start">
            <div
              className="relative overflow-hidden rounded-2xl bg-[#FAF8F4]"
              style={{ aspectRatio: "1/1", cursor: allImages.length > 0 ? "zoom-in" : "default" }}
              onClick={() => { if (allImages.length > 0) setLightboxOpen(true); }}
            >
              {allImages.length > 0 ? (
                <SafeImage
                  src={allImages[selectedImage]}
                  alt={combo.name}
                  className="h-full w-full object-cover transition-opacity duration-200"
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <span className="text-7xl font-black text-[#1D1D1D]/10">
                    {combo.name.slice(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
              {discountPct > 0 && (
                <div className="absolute right-3 top-3 rounded-lg px-2.5 py-1 text-[11px] font-bold text-white bg-[#F26250]">
                  {discountPct}% OFF
                </div>
              )}
              {combo.tag && (
                <div className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.6px] text-white bg-[#F26250]">
                  {combo.tag}
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {allImages.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className="relative flex-shrink-0 overflow-hidden rounded-xl transition-all"
                    style={{
                      width: 72, height: 72,
                      border: `2px solid ${i === selectedImage ? "#F26250" : "transparent"}`,
                      background: "#FAF8F4",
                      opacity: i === selectedImage ? 1 : 0.55,
                    }}
                  >
                    <SafeImage src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: combo info */}
          <div className="flex flex-col gap-5">

            {/* Bundle badge */}
            <div className="self-start rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
              style={{ background: "#FEF0EC", color: "#D94A39" }}>
              Bundle Deal
            </div>

            {/* Name */}
            <h1 className="text-[1.9rem] font-bold leading-tight tracking-[-0.02em] sm:text-[2.2rem]"
              style={{ color: "#1D1D1D" }}>
              {combo.name}
            </h1>

            {/* Pricing */}
            <div className="flex flex-wrap items-center gap-3">
              {mrp > sellingPrice && (
                <span className="text-[15px] text-[#8A8A85] line-through">
                  ₹{mrp.toLocaleString("en-IN")}
                </span>
              )}
              <span className="text-[2rem] font-black leading-none tracking-[-0.03em]" style={{ color: "#1D1D1D" }}>
                ₹{sellingPrice.toLocaleString("en-IN")}
              </span>
              {savings > 0 && (
                <span className="rounded-lg px-3 py-1 text-[12px] font-bold text-white bg-[#F26250]">
                  SAVE ₹{savings.toLocaleString("en-IN")}
                </span>
              )}
            </div>

            {/* Stock status */}
            {!inStock && (
              <div className="rounded-xl border border-[#FECDCA] bg-[#FFF5F4] px-4 py-3 text-[13px] font-semibold text-[#F26250]">
                Currently out of stock
              </div>
            )}

            {/* What's included */}
            {combo.items.length > 0 && (
              <div className="rounded-2xl border border-[#EDE9E1] bg-[#FDFAF6] p-4">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-[#8A8A85]">
                  What&apos;s included
                </p>
                <div className="space-y-2.5">
                  {combo.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                          style={{ background: "#F26250" }}>
                          {i + 1}
                        </span>
                        <div>
                          <p className="text-[12.5px] font-semibold text-[#1D1D1D]">
                            {item.name || item.sku || `Item ${i + 1}`}
                          </p>
                          <p className="text-[11px] text-[#8A8A85]">Qty: {item.quantity}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[13px] font-bold text-[#1D1D1D]">
                          ₹{item.sellingPrice.toLocaleString("en-IN")}
                        </p>
                        {item.mrp > item.sellingPrice && (
                          <p className="text-[11px] text-[#B0ABA3] line-through">
                            ₹{item.mrp.toLocaleString("en-IN")}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Savings summary row */}
                {savings > 0 && (
                  <div className="mt-3 flex items-center justify-between border-t border-dashed border-[#E8E3DA] pt-3">
                    <span className="text-[12px] font-semibold text-[#8A8A85]">Bundle savings</span>
                    <span className="text-[13px] font-bold text-[#22A366]">
                      − ₹{savings.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {combo.description && (
              <ProductDescription
                description={combo.description}
                expanded={descExpanded}
                onToggle={() => setDescExpanded(v => !v)}
              />
            )}

            {/* CTA buttons */}
            {cartQty === 0 ? (
              <>
                <button
                  onClick={handleAddToCart}
                  disabled={!inStock}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 text-[15px] font-bold transition disabled:opacity-40"
                  style={{ height: 52, borderColor: "#F26250", color: "#F26250", background: "#FEF6F4" }}
                  onMouseEnter={e => { if (inStock) e.currentTarget.style.background = "#FBE4DF"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#FEF6F4"; }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  Add bundle to cart
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={!inStock}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-bold text-white transition disabled:opacity-40"
                  style={{ height: 52, background: "#F26250", boxShadow: "0 6px 18px rgba(242, 98, 80,0.28)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#D94A39"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#F26250"; }}
                >
                  Buy it now
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            ) : (
              <>
                <div className="flex w-full items-center overflow-hidden rounded-2xl border-2 border-[#F26250]"
                  style={{ height: 52, background: "#FEF6F4" }}>
                  <button
                    onClick={() => handleQtyChange(cartQty - 1)}
                    className="flex h-full flex-1 items-center justify-center text-xl font-bold transition hover:bg-[#FBE4DF]"
                    style={{ color: "#F26250" }}>
                    −
                  </button>
                  <span className="flex min-w-[52px] items-center justify-center gap-1.5 text-[15px] font-bold text-[#1D1D1D]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F26250" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>
                    {cartQty}
                  </span>
                  <button
                    onClick={() => handleQtyChange(cartQty + 1)}
                    className="flex h-full flex-1 items-center justify-center text-xl font-bold transition hover:bg-[#FBE4DF]"
                    style={{ color: "#F26250" }}>
                    +
                  </button>
                </div>
                <Link
                  href={`/shop/checkout?prefix=${prefix}`}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-bold text-white transition"
                  style={{ height: 52, background: "#F26250", boxShadow: "0 6px 18px rgba(242, 98, 80,0.28)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#D94A39"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#F26250"; }}
                >
                  Proceed to Checkout
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </Link>
              </>
            )}

            <ProductHighlights
              badges={storeHighlights(tenant.additionalInfo?.landingPage?.storefront)}
              minCartValue={outlet?.minCartValue ?? null}
            />
          </div>
        </div>

        {/* You may also like */}
        {products.length > 0 && (
          <div className="mt-16 border-t border-[#F0EDE6] pt-12">
            <h2 className="mb-6 text-[1.4rem] font-bold text-[#1D1D1D]">You may also like</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {products.slice(0, 5).map((p) => {
                const pMrp = p.pricing?.mrp ?? 0;
                const pSelling = p.pricing?.offerPrice ?? p.pricing?.sellingPrice ?? 0;
                const pDiscount = pMrp > pSelling && pMrp > 0
                  ? Math.round(((pMrp - pSelling) / pMrp) * 100) : 0;
                const pCartQty = getItemQuantity(p._id);
                const pSoldOut = isProductSoldOut({
                  stock: p.inventory?.quantity,
                  variantCount: p.variantCount,
                  variants: p.variants,
                });

                return (
                  <div
                    key={p._id}
                    className="group relative cursor-pointer overflow-hidden rounded-2xl border border-[#F0EDE6] bg-white transition hover:border-[#E8E3DA] hover:shadow-md"
                  >
                    {/* Stretched link — see AllProducts for why this is an overlay. */}
                    <Link href={`/shop/product/${p._id}?prefix=${prefix}&outletId=${outletId}`}
                      aria-label={p.name} className="absolute inset-0 z-[1]" />
                    <div className="relative overflow-hidden bg-[#FAF8F4]" style={{ aspectRatio: "1/1" }}>
                      {p.images?.thumbnail ? (
                        <SafeImage
                          src={p.images.thumbnail}
                          alt={p.name}
                          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <span className="text-3xl font-black text-[#1D1D1D]/10">
                            {p.name.slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )}
                      {pDiscount > 0 && (
                        <div className="absolute left-2 top-2 rounded-md px-2 py-0.5 text-[10px] font-bold text-white bg-[#F26250]">
                          {pDiscount}% OFF
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="line-clamp-2 text-[12.5px] font-semibold leading-snug text-[#1D1D1D]">{p.name}</p>
                      {p.inventory?.unit && (
                        <p className="mt-0.5 text-[11px] text-[#8A8A85]">{p.inventory.unit}</p>
                      )}
                      <div className="mt-2 flex items-center justify-between gap-1">
                        <div>
                          <span className="text-[13px] font-bold text-[#1D1D1D]">₹{pSelling}</span>
                          {pMrp > pSelling && (
                            <span className="ml-1 text-[11px] text-[#B0ABA3] line-through">₹{pMrp}</span>
                          )}
                        </div>
                        {pSoldOut ? (
                          <span
                            aria-label="Out of stock"
                            className="relative z-[2] cursor-not-allowed rounded-lg border px-2 py-1 text-[10px] font-bold uppercase"
                            style={{ borderColor: "var(--border)", color: "var(--techno-black-50)", background: "var(--surface-2)" }}
                          >
                            Sold out
                          </span>
                        ) : pCartQty > 0 ? (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="relative z-[2] flex h-7 w-[68px] overflow-hidden rounded-lg bg-[#F26250]"
                          >
                            <button
                              onClick={(e) => { e.stopPropagation(); handleProductQtyChange(p._id, pCartQty - 1); }}
                              className="flex flex-1 items-center justify-center text-xs text-white hover:bg-white/10"
                            >
                              −
                            </button>
                            <span className="flex min-w-[22px] items-center justify-center text-[11px] font-bold text-white">
                              {pCartQty}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleProductQtyChange(p._id, pCartQty + 1); }}
                              className="flex flex-1 items-center justify-center text-xs text-white hover:bg-white/10"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleProductAdd(p); }}
                            className="relative z-[2] flex h-7 w-7 items-center justify-center rounded-lg border-2 text-base font-bold transition"
                            style={{ borderColor: "#F26250", color: "#F26250" }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#F26250";
                              e.currentTarget.style.color = "#fff";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.color = "#F26250";
                            }}
                          >
                            +
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#F0EDE6] bg-white/95 px-4 py-3 backdrop-blur-sm md:hidden">
        {cartQty === 0 ? (
          <div className="flex gap-3">
            <button
              onClick={handleAddToCart}
              disabled={!inStock}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl border-2 text-[14px] font-bold transition disabled:opacity-40"
              style={{ borderColor: "#F26250", color: "#F26250", background: "#FEF6F4" }}
            >
              Add bundle
            </button>
            <button
              onClick={handleBuyNow}
              disabled={!inStock}
              className="flex h-12 flex-1 items-center justify-center rounded-2xl text-[14px] font-bold text-white transition disabled:opacity-40"
              style={{ background: "#F26250" }}
            >
              Buy now
            </button>
          </div>
        ) : (
          <div className="flex gap-3">
            <div className="flex h-12 w-[120px] shrink-0 items-center overflow-hidden rounded-2xl border-2 border-[#1D1D1D]">
              <button onClick={() => handleQtyChange(cartQty - 1)}
                className="flex flex-1 items-center justify-center text-lg text-[#1D1D1D]">−</button>
              <span className="min-w-[32px] text-center text-[14px] font-bold text-[#1D1D1D]">{cartQty}</span>
              <button onClick={() => handleQtyChange(cartQty + 1)}
                className="flex flex-1 items-center justify-center text-lg text-[#1D1D1D]">+</button>
            </div>
            <Link
              href={`/shop/checkout?prefix=${prefix}`}
              className="flex flex-1 h-12 items-center justify-center rounded-2xl text-[14px] font-bold text-white"
              style={{ background: "#F26250" }}
            >
              Checkout →
            </Link>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: "rgba(10,10,10,0.92)", backdropFilter: "blur(6px)" }}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-white"
            style={{ background: "rgba(255,255,255,0.12)" }}
            onClick={() => setLightboxOpen(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {allImages.length > 1 && selectedImage > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full text-white"
              style={{ background: "rgba(255,255,255,0.12)" }}
              onClick={(e) => { e.stopPropagation(); setSelectedImage(i => i - 1); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}

          {allImages.length > 1 && selectedImage < allImages.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full text-white"
              style={{ background: "rgba(255,255,255,0.12)" }}
              onClick={(e) => { e.stopPropagation(); setSelectedImage(i => i + 1); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}

          <SafeImage
            src={allImages[selectedImage]}
            alt={combo.name}
            className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {allImages.length > 1 && (
            <div className="absolute bottom-5 flex gap-2" onClick={(e) => e.stopPropagation()}>
              {allImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className="overflow-hidden rounded-xl transition-all"
                  style={{
                    width: 52, height: 52,
                    border: `2px solid ${i === selectedImage ? "#F26250" : "rgba(255,255,255,0.25)"}`,
                    opacity: i === selectedImage ? 1 : 0.5,
                  }}
                >
                  <SafeImage src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
